import { SectionCard } from '@atlas/ui-components'
import { Avatar, ListBoxItem, Select } from '@atlas/ui-primitives'
import { initials } from '@/lib/labels'

export interface DesignerOption {
	userId: string
	name: string
	/** The person's job function, e.g. `Designer`; absent when they have none. */
	role: string | null
}

function Person({ name, role }: Omit<DesignerOption, 'userId'>) {
	return (
		<span className="flex min-w-0 items-center gap-3 text-left">
			<Avatar size="sm" fallback={initials(name)} aria-hidden className="bg-ds-warning/20 text-ds-xs text-ds-text" />
			<span className="min-w-0">
				<span className="block truncate text-sm font-semibold text-ds-text">{name}</span>
				{role ? <span className="block truncate text-xs text-ds-text-muted">{role}</span> : null}
			</span>
		</span>
	)
}

const DESIGNER_SLOT = { title: 'Assign designer', label: 'Designer', placeholder: 'Choose a designer' }

/** A card for choosing one person on the order, the designer unless told otherwise; picking one assigns them at once. */
export function AssignDesignerCard({
	options,
	value,
	onChange,
	slot = DESIGNER_SLOT,
}: {
	options: DesignerOption[]
	/** The current holder's user id, or null while the order has none. */
	value: string | null
	onChange: (userId: string) => void
	/** Who the card assigns, as its title, field name and empty prompt say it. */
	slot?: typeof DESIGNER_SLOT
}) {
	return (
		<SectionCard title={slot.title} aria-label={slot.title} collapsible>
			<Select
				aria-label={slot.label}
				placeholder={slot.placeholder}
				selectedKey={value}
				onSelectionChange={(key) => onChange(String(key))}
				className="h-auto min-h-14 py-2"
			>
				{options.map((option) => (
					<ListBoxItem
						key={option.userId}
						id={option.userId}
						textValue={option.name}
						className="cursor-pointer rounded-ds-sm px-2 py-1.5 outline-none data-[focused]:bg-ds-surface"
					>
						<Person name={option.name} role={option.role} />
					</ListBoxItem>
				))}
			</Select>
		</SectionCard>
	)
}
